import {
  Button,
  Dropdown,
  Empty,
  Input,
  Select,
  Space,
  Spin,
  Tag,
  Tooltip,
  Typography,
  Badge,
  Avatar,
  Segmented,
} from "antd";
import { ApiService } from "api/api.service";
import { OrgSelector } from "components/Organisation/OrganisationSelector";
import PageTitle from "components/PageTitle";
import { CreateTemplateModal } from "components/TaskTemplate/CreateTemplateModal";
import { TaskBoardTemplateListItemDto } from "dtos/tasks/task-template.dto";
import { Resource } from "dtos/stage.dto";
import DashboardLayout from "layout/DashboardLayout";
import { useState, useMemo } from "react";
import {
  TbCopy,
  TbDots,
  TbExternalLink,
  TbLayoutColumns,
  TbLoader2,
  TbPlus,
  TbSearch,
  TbSubtask,
  TbTemplate,
  TbTrash,
  TbMap,
  TbChecklist,
  TbClock,
  TbFilter,
  TbX,
  TbChevronDown,
  TbUsers,
  TbCalendarTime,
} from "react-icons/tb";
import { useNavigate, useSearchParams } from "react-router-dom";
import { formatDateTime } from "utils/functions";
import { storedTokens } from "utils/storedTokens";
import { theme } from "antd";
import { MdAutoMode } from "react-icons/md";
import { resolveStageColor, StageColor } from "components/Stages/constants";

const { useToken } = theme;

const RESOURCE_COLOR: Record<Resource, string> = {
  [Resource.LOAN]: "blue",
  [Resource.APPLICATION]: "green",
};

const RESOURCE_SWATCH: Record<Resource, { bg: string; text: string; border: string; gradient: string }> = {
  [Resource.LOAN]: {
    bg: "#f0fdfa",
    text: "#0f766e",
    border: "#5eead4",
    gradient: "linear-gradient(135deg, #f0fdfa 0%, #ccfbf1 100%)",
  },
  [Resource.APPLICATION]: {
    bg: "#f5f3ff",
    text: "#6d28d9",
    border: "#c4b5fd",
    gradient: "linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)",
  },
};

const RESOURCE_OPTIONS = [
  { label: "Application", value: Resource.APPLICATION },
  { label: "Loan", value: Resource.LOAN },
];

// ─── Stat Pill ────────────────────────────────────────────────────────────────

interface StatPillProps {
  icon: React.ReactNode;
  count: number;
  label: string;
}

/**
 * Icon + count badge used in the template card stats strip.
 * Borderless, no shadow — uses fill only.
 */
const StatPill = ({ icon, count, label }: StatPillProps) => {
  const { token } = useToken();

  return (
    <Tooltip title={label}>
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 4,
          padding: "2px 8px",
          borderRadius: token.borderRadiusSM,
          background: token.colorFillTertiary,
          fontSize: 12,
          color: token.colorTextSecondary,
          cursor: "default",
          userSelect: "none",
          border: `1px solid ${token.colorBorderSecondary}`,
          transition: "all 0.2s",
        }}
        className="hover:bg-gray-50"
      >
        {icon}
        <span style={{ fontVariantNumeric: "tabular-nums", fontWeight: 500 }}>{count}</span>
      </span>
    </Tooltip>
  );
};

// Template Card ------>

interface TemplateCardProps {
  template: TaskBoardTemplateListItemDto;
  onView: () => void;
}

const TemplateCard = ({ template, onView }: TemplateCardProps) => {
  const { token } = useToken();
  const swatch = RESOURCE_SWATCH[template.resource];

  return (
    <div
      onClick={onView}
      className="group relative flex flex-col rounded-xl cursor-pointer bg-white border border-solid border-gray-200 transition-all duration-300 hover:border-indigo-400 hover:shadow-lg hover:shadow-indigo-100/50 hover:-translate-y-0.5 overflow-hidden"
    >
      {/* Accent bar on top */}
      <div
        className="absolute top-0 left-0 right-0 h-0.5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
        // style={{ background: swatch.gradient }}
      />

      {/* ── Body ── */}
      <div className="flex flex-col p-5 gap-4 flex-1">
        {/* Top row — icon + name + resource badge + dots */}
        <div className="flex items-start gap-3">
          {/* Animated icon container */}
          <div
            className="relative flex items-center justify-center shrink-0 transition-transform duration-300 group-hover:scale-105"
            style={{
              width: 44,
              height: 44,
              borderRadius: 12,
              background: swatch.gradient,
              color: swatch.text,
              boxShadow: `inset 0 0 0 1px ${swatch.border}`,
            }}
          >
            <TbSubtask size={22} />
            {/* Subtle glow on hover */}
            <div
              className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"
              style={{ boxShadow: `0 0 20px ${swatch.border}40` }}
            />
          </div>

          {/* Name and resource info */}
          <div className="flex-1 min-w-0">
            <Typography.Text
              strong
              className="!text-sm !leading-snug block group-hover:!text-indigo-600 transition-colors duration-200"
              ellipsis={{ tooltip: template.name }}
            >
              {template.name}
            </Typography.Text>
            <div className="flex items-center gap-2 mt-1">
              <span
                className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium"
                style={{
                  background: swatch.bg,
                  border: `1px solid ${swatch.border}`,
                  color: swatch.text,
                  lineHeight: "1.2",
                }}
              >
                {template.resource}
              </span>
              {/* {template.autoStart && (
                                <span className="inline-flex items-center gap-0.5 text-xs text-amber-600">
                                    <MdAutoMode size={12} />
                                    <span className="font-medium">Auto</span>
                                </span>
                            )} */}
            </div>
          </div>

          {/* Dots menu */}
          <Dropdown
            trigger={["click"]}
            placement="bottomRight"
            menu={{
              items: [
                {
                  key: "view",
                  icon: <TbExternalLink size={14} />,
                  label: "View Details",
                  onClick: ({ domEvent }) => {
                    domEvent.stopPropagation();
                    onView();
                  },
                },
                { type: "divider" },
                {
                  key: "duplicate",
                  icon: <TbCopy size={14} />,
                  label: "Duplicate Template",
                  disabled: true,
                },
                {
                  key: "delete",
                  icon: <TbTrash size={14} />,
                  label: "Delete Template",
                  disabled: true,
                  danger: true,
                },
              ],
            }}
          >
            <Button
              type="text"
              size="small"
              icon={<TbDots size={16} />}
              onClick={(e) => e.stopPropagation()}
              className="shrink-0 !text-gray-400 hover:!bg-gray-100 hover:!text-gray-600 transition-colors"
            />
          </Dropdown>
        </div>

        {/* Divider */}
        <div className="h-px" style={{ background: token.colorBorderSecondary }} />

        {/* Group Strip */}
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium uppercase tracking-wide" style={{ color: token.colorTextTertiary }}>
              Workflow Groups
            </span>
            <span className="text-xs" style={{ color: token.colorTextQuaternary }}>
              {template.groups.length} {template.groups.length === 1 ? "group" : "groups"}
            </span>
          </div>

          {template.groups.length === 0 ? (
            <div className="flex items-center gap-2 py-2">
              <TbLayoutColumns size={14} style={{ color: token.colorTextQuaternary }} />
              <span className="text-xs italic" style={{ color: token.colorTextQuaternary }}>
                No groups configured yet
              </span>
            </div>
          ) : (
            <div className="flex items-center gap-1.5 flex-wrap">
              {template.groups.slice(0, 3).map((group) => (
                <span
                  key={String(group._id)}
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium transition-colors hover:bg-gray-100"
                  style={{
                    background: token.colorFillTertiary,
                    color: token.colorTextSecondary,
                    border: `1px solid ${token.colorBorderSecondary}`,
                  }}
                >
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: swatch.border }} />
                  {group.name}
                </span>
              ))}
              {template.groups.length > 3 && (
                <Tooltip
                  title={
                    <div className="flex flex-col gap-1">
                      {template.groups.slice(3).map((g) => (
                        <span key={String(g._id)}>{g.name}</span>
                      ))}
                    </div>
                  }
                >
                  <span
                    className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium cursor-pointer hover:bg-gray-100 transition-colors"
                    style={{
                      background: token.colorFillTertiary,
                      color: token.colorTextSecondary,
                      border: `1px solid ${token.colorBorderSecondary}`,
                    }}
                  >
                    +{template.groups.length - 3} more
                  </span>
                </Tooltip>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── Footer ── */}
      <div
        className="flex items-center justify-between px-5 py-3 transition-colors"
        style={{
          borderTop: `1px solid ${token.colorBorderSecondary}`,
          background: token.colorFillQuaternary,
        }}
      >
        <span className="inline-flex items-center gap-1.5" style={{ color: token.colorTextTertiary }}>
          <TbClock size={13} />
          <Typography.Text className="!text-xs" style={{ color: token.colorTextTertiary }}>
            {formatDateTime(template.updatedAt ?? template.createdAt)}
          </Typography.Text>
        </span>

        <div className="flex items-center gap-2">
          {template.autoStart && (
            <Tooltip title="Auto-start enabled: Tasks activate when conditions are met">
              <span
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium transition-all hover:scale-105"
                style={{
                  background: "#fffbeb",
                  border: "1px solid #fde68a",
                  color: "#b45309",
                }}
              >
                <MdAutoMode size={12} className="animate-pulse" />
                <span className="font-semibold">Auto</span>
              </span>
            </Tooltip>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Page ─────────────────────────────────────────────────────────────────────

/**
 * List page for task board templates.
 * Renders templates as a responsive card grid.
 * Accessible at /dashboard/operations/tasks
 */
function TaskTemplateListPage() {
  const navigate = useNavigate();
  const { token } = useToken();
  const [sp, setSp] = useSearchParams();

  const [search, setSearch] = useState("");
  const [resource, setResource] = useState<Resource | "">(Resource.APPLICATION);
  const [createOpen, setCreateOpen] = useState(false);
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");

  const [selectedOrg, setSelectedOrg] = useState(() => {
    const queryOrgCode = sp.get("orgCode");
    return (queryOrgCode || storedTokens.orgCode.getToken()) ?? undefined;
  });

  const organisations = ApiService.organisation.useGetAllOrganisations();

  const templatesQuery = ApiService.taskTemplate.useGetPaginated({
    page: 1,
    limit: 100,
    search,
    orgCode: selectedOrg,
    resource: resource || undefined,
  });

  const templates = templatesQuery.data?.result.data ?? [];

  // Calculate stats
  const stats = useMemo(() => {
    const totalGroups = templates.reduce((acc, t) => acc + t.groups.length, 0);
    const autoStartCount = templates.filter((t) => t.autoStart).length;

    return { totalGroups, autoStartCount };
  }, [templates]);

  const hasActiveFilters = search !== "" || resource !== "";

  return (
    <DashboardLayout
      headerRight={
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <OrgSelector
            selected={selectedOrg}
            onChange={(org) => {
              setSp((prev) => {
                const next = new URLSearchParams(prev);
                next.set("orgCode", org.code);
                return next;
              });
              setSelectedOrg(org.code);
            }}
            organisations={organisations.data ?? []}
          />
          <Button
            type="primary"
            icon={<TbPlus size={15} />}
            onClick={() => setCreateOpen(true)}
            className="!shadow-sm hover:!shadow-md transition-shadow"
          >
            New Template
          </Button>
        </div>
      }
    >
      <PageTitle title="Task Templates" pageTitle={[{ title: "Operations" }, { title: "Task Templates" }]} />

      {/* ── Stats Bar ── */}
      {/* {!templatesQuery.isLoading && templates.length > 0 && (
                <div
                    className="flex items-center gap-4 px-5 py-3"
                    style={{
                        borderBottom: `1px solid ${token.colorBorderSecondary}`,
                        background: token.colorBgContainer,
                    }}
                >
                    <div className="flex items-center gap-6">
                        <div className="flex items-center gap-2">
                            <TbTemplate size={16} style={{ color: token.colorTextTertiary }} />
                            <span className="text-sm font-medium">{templates.length}</span>
                            <span className="text-sm" style={{ color: token.colorTextTertiary }}>Templates</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <TbLayoutColumns size={16} style={{ color: token.colorTextTertiary }} />
                            <span className="text-sm font-medium">{stats.totalGroups}</span>
                            <span className="text-sm" style={{ color: token.colorTextTertiary }}>Groups</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <TbChecklist size={16} style={{ color: token.colorTextTertiary }} />
                            <span className="text-sm" style={{ color: token.colorTextTertiary }}>Tasks</span>
                        </div>
                        {stats.autoStartCount > 0 && (
                            <div className="flex items-center gap-2">
                                <MdAutoMode size={16} style={{ color: '#b45309' }} />
                                <span className="text-sm font-medium">{stats.autoStartCount}</span>
                                <span className="text-sm" style={{ color: token.colorTextTertiary }}>Auto-start</span>
                            </div>
                        )}
                    </div>
                </div>
            )} */}

      {/* ── Filter bar ── */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          padding: "14px 20px",
          borderBottom: `1px solid ${token.colorBorderSecondary}`,
          background: token.colorBgContainer,
        }}
      >
        <Input
          prefix={<TbSearch size={15} color={token.colorTextQuaternary} />}
          placeholder="Search templates by name..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          allowClear
          style={{ maxWidth: 280 }}
          className="!rounded-lg"
        />

        <Select
          value={resource}
          onChange={(val) => setResource(val)}
          options={RESOURCE_OPTIONS}
          style={{ width: 150 }}
          className="!rounded-lg"
          placeholder="Resource type"
        />

        <div className="flex-1" />

        {/* {hasActiveFilters && (
          <Button
            type="text"
            size="small"
            icon={<TbX size={14} />}
            onClick={() => {
              setSearch("");
            }}
            className="!text-gray-500 hover:!text-gray-700"
          >
            Clear Filters
          </Button>
        )} */}

        <Segmented
          value={viewMode}
          onChange={(val) => setViewMode(val as "grid" | "list")}
          options={[
            { value: "grid", icon: <TbLayoutColumns size={14} /> },
            { value: "list", icon: <TbChecklist size={14} /> },
          ]}
          size="small"
        />
      </div>

      {/* ── Content ── */}
      <div
        style={{
          padding: "20px",
          overflowY: "auto",
          height: `calc(100vh - 64px - 53px${templates.length > 0 ? " - 53px" : ""})`,
          background: token.colorBgLayout,
        }}
      >
        {templatesQuery.isLoading ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
            }}
          >
            <div className="flex flex-col items-center gap-3">
              <Spin indicator={<TbLoader2 size={36} className="animate-spin text-indigo-500" />} />
              <Typography.Text type="secondary" className="text-sm">
                Loading templates...
              </Typography.Text>
            </div>
          </div>
        ) : templates.length === 0 ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              gap: 16,
            }}
          >
            <div
              className="flex items-center justify-center"
              style={{
                width: 80,
                height: 80,
                borderRadius: "50%",
                background: token.colorFillTertiary,
              }}
            >
              <TbTemplate size={40} style={{ color: token.colorTextQuaternary }} />
            </div>
            <div className="text-center">
              <Typography.Title level={5} className="!mb-1">
                {search || resource ? "No matching templates" : "Create your first template"}
              </Typography.Title>
              <Typography.Text type="secondary" className="text-sm">
                {search || resource
                  ? "Try adjusting your search or filters"
                  : "Start by creating a template to organize your workflow"}
              </Typography.Text>
            </div>
            {!search && !resource && (
              <Button type="primary" icon={<TbPlus size={14} />} onClick={() => setCreateOpen(true)} className="!mt-2">
                New Template
              </Button>
            )}
          </div>
        ) : viewMode === "grid" ? (
          /* ── Card grid ── */
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
              gap: 16,
              alignContent: "start",
            }}
          >
            {templates.map((template) => (
              <TemplateCard
                key={String(template._id)}
                template={template}
                onView={() => navigate(`/dashboard/operations/tasks/${template._id}`)}
              />
            ))}
          </div>
        ) : (
          /* ── List view ── */
          <div className="flex flex-col gap-2">
            {templates.map((template) => (
              <div
                key={String(template._id)}
                onClick={() => navigate(`/dashboard/operations/tasks/${template._id}`)}
                className="flex items-center gap-4 p-4 rounded-lg bg-white border border-solid border-gray-200 cursor-pointer hover:border-indigo-400 hover:shadow-sm transition-all"
              >
                <div
                  className="flex items-center justify-center shrink-0"
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 8,
                    background: RESOURCE_SWATCH[template.resource].bg,
                    color: RESOURCE_SWATCH[template.resource].text,
                  }}
                >
                  <TbSubtask size={18} />
                </div>

                <div className="flex-1 min-w-0">
                  <Typography.Text strong className="!text-sm block truncate">
                    {template.name}
                  </Typography.Text>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-xs" style={{ color: token.colorTextTertiary }}>
                      {template.groups.length} groups
                    </span>
                    <span className="text-xs" style={{ color: token.colorTextQuaternary }}>
                      •
                    </span>
                    <span className="text-xs" style={{ color: token.colorTextTertiary }}>
                      Updated {formatDateTime(template.updatedAt ?? template.createdAt)}
                    </span>
                  </div>
                </div>

                <span
                  className="px-2 py-1 rounded text-xs font-medium"
                  style={{
                    background: RESOURCE_SWATCH[template.resource].bg,
                    border: `1px solid ${RESOURCE_SWATCH[template.resource].border}`,
                    color: RESOURCE_SWATCH[template.resource].text,
                  }}
                >
                  {template.resource}
                </span>

                {template.autoStart && (
                  <Tooltip title="Auto-start enabled">
                    <MdAutoMode size={16} className="text-amber-600" />
                  </Tooltip>
                )}

                <Button type="text" size="small" icon={<TbChevronDown size={14} />} className="!text-gray-400" />
              </div>
            ))}
          </div>
        )}
      </div>

      <CreateTemplateModal open={createOpen} onClose={() => setCreateOpen(false)} />
    </DashboardLayout>
  );
}

export default TaskTemplateListPage;
